import type { Nurse, NurseInShift } from "../../utils/models";
import type { SystemAutoCompleteOption } from "../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";

/**
 * One row of the "+" dialog's staff picker: a person, and — for someone already on the day — the one shift of
 * theirs this row stands for. `key` is the row's identity (several rows share the person), which the picker
 * compares by, so picking one row never selects or highlights another.
 */
export type QuickAddStaffOption = SystemAutoCompleteOption<Nurse> & { key: string; shift: NurseInShift | undefined };

/**
 * The picker's rows: one per same-day shift for someone already scheduled (earliest first — the order the day's
 * map holds them in), so a person working a double appears twice, each row with its own shift; one plain row
 * for everyone else. Every row is labelled with the person's name, so searching by name finds all of theirs.
 */
export const buildQuickAddStaffOptions = (
	nurses: readonly Nurse[],
	shiftsByNurse: ReadonlyMap<number, readonly NurseInShift[]>,
	formatName: (nurse: Nurse) => string,
): QuickAddStaffOption[] =>
	nurses.flatMap((nurse): QuickAddStaffOption[] => {
		const label = formatName(nurse);
		const shifts = shiftsByNurse.get(nurse.id);
		if (!shifts?.length) return [{ label, value: nurse, key: String(nurse.id), shift: undefined }];
		return shifts.map((shift) => ({ label, value: nurse, key: `${nurse.id}:${shift.id}`, shift }));
	});
